// UnifiedTimeline.tsx - Single threaded "Storyline" for all contact interactions
// Combines Notes, SMS, Voice Transcripts into a messenger-style timeline

import { useEffect, useState } from "react";
import { useGetList } from "ra-core";
import { format, formatDistanceToNow } from "date-fns";
import {
    MessageSquare,
    Phone,
    PhoneIncoming,
    PhoneOutgoing,
    StickyNote,
    Bot,
    Mail,
    Play,
    Pause,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { ContactNote } from "../types";
import { TimelineNoteCreate } from "./TimelineNoteCreate";

// Unified timeline item type
interface TimelineItem {
    id: string;
    type: "note" | "sms" | "call" | "email";
    direction?: "inbound" | "outbound";
    timestamp: Date;
    content: string;
    status?: string;
    aiGenerated?: boolean;
    recordingUrl?: string;
    durationSeconds?: number;
    botId?: string;
    metadata?: Record<string, unknown>;
    // Original record for editing
    originalRecord?: ContactNote | SMSMessage;
}

interface SMSMessage {
    id: number;
    created_at: string;
    contact_id: number;
    direction: "inbound" | "outbound";
    content: string;
    status: "pending" | "queued" | "sent" | "delivered" | "failed";
    phone_from: string;
    phone_to: string;
    ai_generated: boolean;
    delivered_at?: string;
    error_message?: string;
}

interface UnifiedTimelineProps {
    contactId: number;
    maxItems?: number;
}

// Channel configuration for color-coded markers
const channelConfig = {
    note: {
        color: "border-l-gray-400",
        bgColor: "bg-gray-50",
        icon: StickyNote,
        label: "Note",
    },
    sms: {
        color: "border-l-blue-500",
        bgColor: "bg-blue-50",
        icon: MessageSquare,
        label: "SMS",
    },
    call: {
        color: "border-l-green-500",
        bgColor: "bg-green-50",
        icon: Phone,
        label: "Call",
    },
    email: {
        color: "border-l-purple-500",
        bgColor: "bg-purple-50",
        icon: Mail,
        label: "Email",
    },
};

export const UnifiedTimeline = ({ contactId, maxItems = 50 }: UnifiedTimelineProps) => {
    const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [playingAudio, setPlayingAudio] = useState<string | null>(null);
    const [activeFilter, setActiveFilter] = useState<"all" | "note" | "sms" | "call" | "email">("all");

    // Fetch notes
    const { data: notes, isPending: notesPending } = useGetList<ContactNote>(
        "contactNotes",
        {
            filter: { contact_id: contactId },
            sort: { field: "date", order: "DESC" },
            pagination: { page: 1, perPage: maxItems },
        }
    );

    // Fetch SMS messages
    const { data: smsMessages, isPending: smsPending } = useGetList<SMSMessage>(
        "sms_messages",
        {
            filter: { contact_id: contactId },
            sort: { field: "created_at", order: "DESC" },
            pagination: { page: 1, perPage: maxItems },
        }
    );

    // Merge and sort timeline items
    useEffect(() => {
        const items: TimelineItem[] = [];

        // Add notes to timeline
        notes?.forEach((note) => {
            const interactionType = note.interaction_type || "note";
            let type: TimelineItem["type"] = "note";

            if (interactionType === "call" || (interactionType as string) === "voicebot") {
                type = "call";
            } else if (interactionType === "email") {
                type = "email";
            }

            items.push({
                id: `note-${note.id}`,
                type,
                direction: note.direction === null ? undefined : note.direction,
                timestamp: new Date(note.date),
                content: note.text,
                status: note.status,
                recordingUrl: note.recording_url,
                durationSeconds: note.duration_seconds,
                botId: note.bot_id,
                metadata: note.metadata,
                originalRecord: note,
            });
        });

        // Add SMS to timeline
        smsMessages?.forEach((sms) => {
            items.push({
                id: `sms-${sms.id}`,
                type: "sms",
                direction: sms.direction,
                timestamp: new Date(sms.created_at),
                content: sms.content,
                status: sms.status,
                aiGenerated: sms.ai_generated,
                originalRecord: sms as any,
            });
        });

        // Sort by timestamp (newest first)
        items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        setTimelineItems(items);
    }, [notes, smsMessages]);

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const isPending = notesPending || smsPending;

    if (isPending) {
        return (
            <Card>
                <CardContent className="py-8">
                    <div className="flex items-center justify-center text-muted-foreground">
                        <div className="animate-pulse">Loading timeline...</div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                    <div className="flex -space-x-1">
                        <div className="w-5 h-5 rounded-full bg-gray-400 flex items-center justify-center">
                            <StickyNote className="w-3 h-3 text-white" />
                        </div>
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                            <MessageSquare className="w-3 h-3 text-white" />
                        </div>
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                            <Phone className="w-3 h-3 text-white" />
                        </div>
                    </div>
                    <span>Activity Timeline</span>
                    {timelineItems.length > 0 && (
                        <Badge variant="secondary" className="ml-2">
                            {timelineItems.length}
                        </Badge>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {/* Filter Chips */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="text-sm text-muted-foreground mr-1">Filter:</span>
                    {(["all", "note", "sms", "call", "email"] as const).map((filter) => {
                        const isActive = activeFilter === filter;
                        const filterConfig = filter === "all"
                            ? { label: "All", bgColor: "bg-slate-100", activeColor: "bg-slate-600 text-white" }
                            : { ...channelConfig[filter], activeColor: channelConfig[filter].color.replace("border-l-", "bg-") + " text-white" };
                        const count = filter === "all"
                            ? timelineItems.length
                            : timelineItems.filter(i => i.type === filter).length;

                        return (
                            <Button
                                key={filter}
                                variant="ghost"
                                size="sm"
                                onClick={() => setActiveFilter(filter)}
                                className={cn(
                                    "h-7 px-3 rounded-full text-xs font-medium transition-all",
                                    isActive
                                        ? filterConfig.activeColor
                                        : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                                )}
                            >
                                {filter === "all" ? "All" : channelConfig[filter].label}
                                {count > 0 && (
                                    <span className={cn(
                                        "ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]",
                                        isActive ? "bg-white/20" : "bg-slate-200"
                                    )}>
                                        {count}
                                    </span>
                                )}
                            </Button>
                        );
                    })}
                </div>

                {/* Add Note Form */}
                <TimelineNoteCreate showStatus className="mb-4" />

                {timelineItems.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                        No interactions yet. Add a note or send an SMS to get started.
                    </div>
                ) : (
                    <div className="space-y-0">
                        {timelineItems
                            .filter(item => activeFilter === "all" || item.type === activeFilter)
                            .map((item, index, filteredItems) => (
                                <TimelineItemCard
                                    key={item.id}
                                    item={item}
                                    isExpanded={expandedIds.has(item.id)}
                                    onToggleExpand={() => toggleExpand(item.id)}
                                    isFirst={index === 0}
                                    isLast={index === filteredItems.length - 1}
                                    playingAudio={playingAudio}
                                    onPlayAudio={setPlayingAudio}
                                />
                            ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

interface TimelineItemCardProps {
    item: TimelineItem;
    isExpanded: boolean;
    onToggleExpand: () => void;
    isFirst: boolean;
    isLast: boolean;
    playingAudio: string | null;
    onPlayAudio: (id: string | null) => void;
}

const TimelineItemCard = ({
    item,
    isExpanded,
    onToggleExpand,
    isFirst: _isFirst,
    isLast,
    playingAudio,
    onPlayAudio,
}: TimelineItemCardProps) => {
    const config = channelConfig[item.type];
    const Icon = config.icon;
    const isLongContent = item.content.length > 150;
    const displayContent = isLongContent && !isExpanded
        ? item.content.substring(0, 150) + "..."
        : item.content;

    // Determine direction icon for calls/sms
    const getDirectionIcon = () => {
        if (item.type === "call") {
            return item.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
        }
        return null;
    };
    const DirectionIcon = getDirectionIcon();

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    return (
        <div className="relative">
            {/* Vertical timeline line */}
            {!isLast && (
                <div
                    className={cn(
                        "absolute left-[19px] top-10 bottom-0 w-0.5",
                        config.color.replace("border-l-", "bg-")
                    )}
                    style={{ opacity: 0.3 }}
                />
            )}

            <div className="flex gap-3 pb-4">
                {/* Timeline dot */}
                <div
                    className={cn(
                        "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
                        config.bgColor,
                        "border-2",
                        config.color.replace("border-l-", "border-")
                    )}
                >
                    {item.botId ? (
                        <Bot className="w-5 h-5 text-purple-600" />
                    ) : DirectionIcon ? (
                        <DirectionIcon className={cn(
                            "w-5 h-5",
                            item.direction === "inbound" ? "text-green-600" : "text-blue-600"
                        )} />
                    ) : (
                        <Icon className={cn(
                            "w-5 h-5",
                            item.type === "sms" ? "text-blue-600" :
                                item.type === "call" ? "text-green-600" :
                                    item.type === "email" ? "text-purple-600" :
                                        "text-gray-600"
                        )} />
                    )}
                </div>

                {/* Content */}
                <div
                    className={cn(
                        "flex-1 rounded-lg p-3 border-l-4",
                        config.color,
                        item.direction === "outbound" ? "bg-slate-50" : "bg-white",
                        "border border-slate-200"
                    )}
                >
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                                {config.label}
                            </Badge>
                            {item.direction && (
                                <Badge variant="secondary" className="text-xs">
                                    {item.direction === "inbound" ? "Received" : "Sent"}
                                </Badge>
                            )}
                            {item.botId && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    AI Bot
                                </Badge>
                            )}
                            {item.aiGenerated && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    AI Generated
                                </Badge>
                            )}
                            {item.status && item.type === "note" && (
                                <Badge variant="outline" className="text-xs capitalize">
                                    {item.status}
                                </Badge>
                            )}
                            {item.durationSeconds && item.durationSeconds > 0 && (
                                <span className="text-xs text-muted-foreground bg-gray-100 px-1.5 py-0.5 rounded">
                                    {formatDuration(item.durationSeconds)}
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                            {formatDistanceToNow(item.timestamp, { addSuffix: true })}
                        </span>
                    </div>

                    {/* Content */}
                    <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {displayContent}
                    </div>

                    {/* Expand/Collapse for long content */}
                    {isLongContent && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onToggleExpand}
                            className="mt-1 h-6 text-xs text-muted-foreground hover:text-foreground"
                        >
                            {isExpanded ? (
                                <>
                                    <ChevronUp className="w-3 h-3 mr-1" /> Show less
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="w-3 h-3 mr-1" /> Show more
                                </>
                            )}
                        </Button>
                    )}

                    {/* Recording player */}
                    {item.recordingUrl && (
                        <div className="mt-2 flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onPlayAudio(playingAudio === item.id ? null : item.id)}
                                className="p-1 h-8 w-8"
                            >
                                {playingAudio === item.id ? (
                                    <Pause className="w-4 h-4 text-green-600" />
                                ) : (
                                    <Play className="w-4 h-4 text-green-600" />
                                )}
                            </Button>
                            {playingAudio === item.id ? (
                                <audio
                                    src={item.recordingUrl}
                                    controls
                                    autoPlay
                                    className="h-8 flex-1"
                                    onEnded={() => onPlayAudio(null)}
                                />
                            ) : (
                                <span className="text-sm text-muted-foreground">
                                    🎙️ Recording available
                                </span>
                            )}
                        </div>
                    )}

                    {/* Timestamp detail on hover */}
                    <div className="mt-1 text-xs text-muted-foreground opacity-60">
                        {format(item.timestamp, "MMM d, yyyy 'at' h:mm a")}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnifiedTimeline;
